import { useEffect, useState, createContext, useContext, useCallback } from "react";
import { Routes, Route, useParams, useNavigate, useLocation, Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ShoppingCart, Store, Search, Star, Plus, Minus, Trash2, User, Package,
  ArrowLeft, X, CheckCircle2, CreditCard, Truck, Clock, LogOut, Home as HomeIcon, LayoutGrid, MessageCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const StoreCtx = createContext(null);
const useStore = () => useContext(StoreCtx);

function waLink(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  if (s.startsWith("http")) return s;
  const digits = s.replace(/[^0-9]/g, "");
  return digits ? `https://wa.me/${digits}` : null;
}

export default function Storefront() {
  const { vendorSlug } = useParams();
  const [store, setStore] = useState(undefined); // undefined=loading, null=notfound
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`cart_${vendorSlug}`) || "[]"); } catch { return []; }
  });
  const [query, setQuery] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [categories, setCategories] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => {
    api.get(`/store/${vendorSlug}`).then((r) => {
      setStore(r.data);
      const c = r.data.branding?.theme_color;
      if (c) document.documentElement.style.setProperty("--store-accent", c);
    }).catch(() => setStore(null));
  }, [vendorSlug]);

  useEffect(() => {
    if (store) api.get(`/store/${vendorSlug}/products`).then((r) => setCategories(r.data.categories || [])).catch(() => {});
  }, [store, vendorSlug]);

  useEffect(() => { localStorage.setItem(`cart_${vendorSlug}`, JSON.stringify(cart)); }, [cart, vendorSlug]);

  const addToCart = useCallback((product, qty = 1, opts = {}) => {
    const variant = opts.variant || null;
    const label = opts.label || product.name;
    const price = opts.price != null ? Number(opts.price) : product.price * (1 - (product.discount || 0) / 100);
    const stock = opts.stock != null ? Number(opts.stock) : Number(product.stock || 0);
    const image = opts.image || product.images?.[0];
    setCart((prev) => {
      const key = product.id + (variant || "");
      const found = prev.find((i) => i.key === key);
      if (found) {
        if (stock && found.qty >= stock) { toast.error(`Only ${stock} in stock`); return prev; }
        const q = stock ? Math.min(found.qty + qty, stock) : found.qty + qty;
        return prev.map((i) => i.key === key ? { ...i, qty: q, stock } : i);
      }
      const q = stock ? Math.min(qty, stock) : qty;
      return [...prev, { key, product_id: product.id, name: label, price, qty: q, variant, image, stock }];
    });
    toast.success(`${label} added to cart`);
  }, []);
  const updateQty = (key, qty) => setCart((prev) => qty <= 0 ? prev.filter((i) => i.key !== key)
    : prev.map((i) => i.key === key ? { ...i, qty: i.stock ? Math.min(qty, i.stock) : qty } : i));
  const clearCart = () => setCart([]);

  if (store === undefined) return <div className="min-h-screen grid place-items-center text-slate-400">Loading store…</div>;
  if (store === null) return (
    <div className="min-h-screen grid place-items-center bg-[#FAF9F6] text-center p-6">
      <div><Store className="h-12 w-12 mx-auto text-slate-300" /><h1 className="font-display text-2xl font-bold mt-4">Store not found</h1><p className="text-slate-500 mt-2">This storefront doesn't exist or isn't available.</p><Link to="/" className="text-rose-600 hover:underline mt-4 inline-block">← Back to Olhub</Link></div>
    </div>
  );

  const accent = store.branding?.theme_color || "#E11D48";
  return (
    <StoreCtx.Provider value={{ store, slug: vendorSlug, cart, addToCart, updateQty, clearCart, accent, query, setQuery, catFilter, setCatFilter, categories, cartOpen, setCartOpen }}>
      <div className="min-h-screen bg-[#FAF9F6] text-[#18181B] pb-20 sm:pb-0">
        <StoreHeader />
        <Routes>
          <Route index element={<Home />} />
          <Route path="product/:pid" element={<ProductDetail />} />
          <Route path="checkout" element={<Checkout />} />
          <Route path="account" element={<Account />} />
          <Route path="login" element={<StoreAuth />} />
        </Routes>
        <StoreFooter />
      </div>
    </StoreCtx.Provider>
  );
}

function StoreHeader() {
  const { store, slug, cart, accent } = useStore();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { query, setQuery } = useStore();
  const count = cart.reduce((a, i) => a + i.qty, 0);
  const isCustomer = user && user.role === "customer";
  const onSearch = (val) => { setQuery(val); if (location.pathname !== `/${slug}`) navigate(`/${slug}`); };
  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-white/90 border-b border-[#E5E5E0]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
        <Link to={`/${slug}`} className="flex items-center gap-2.5 font-display font-bold text-lg shrink-0" data-testid="store-home-link">
          {store.branding?.logo ? <img src={store.branding.logo} alt="" className="h-9 w-9 rounded-lg object-cover" data-testid="store-logo" /> : <div className="h-9 w-9 rounded-lg grid place-items-center text-white" style={{ backgroundColor: accent }}><Store className="h-5 w-5" /></div>}
          <span className="hidden sm:inline">{store.branding?.store_name || slug}</span>
        </Link>
        <div className="relative flex-1 max-w-xl">
          <Search className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
          <Input value={query} onChange={(e) => onSearch(e.target.value)} placeholder="Search products…" className="pl-9 bg-white border-[#E5E5E0]" data-testid="store-search" />
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isCustomer ? (
            <>
              <Button variant="ghost" size="sm" className="hidden sm:flex" onClick={() => navigate(`/${slug}/account`)} data-testid="account-btn"><User className="h-4 w-4 mr-1" />{user.name?.split(" ")[0]}</Button>
              <Button variant="ghost" size="icon" className="hidden sm:flex" onClick={logout} data-testid="store-logout"><LogOut className="h-4 w-4" /></Button>
            </>
          ) : (
            <Button variant="ghost" size="sm" className="hidden sm:flex" onClick={() => navigate(`/${slug}/login`)} data-testid="store-login-btn"><User className="h-4 w-4 mr-1" />Sign in</Button>
          )}
          <CartDrawer count={count} />
        </div>
      </div>
    </header>
  );
}

function CartDrawer({ count }) {
  const { cart, updateQty, slug, accent, cartOpen, setCartOpen } = useStore();
  const { user } = useAuth();
  const navigate = useNavigate();
  const total = cart.reduce((a, i) => a + i.price * i.qty, 0);
  const goCheckout = () => {
    setCartOpen(false);
    if (!user || user.role !== "customer") { toast.info("Please sign in to checkout"); navigate(`/${slug}/login`); return; }
    navigate(`/${slug}/checkout`);
  };
  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" className="relative border-[#E5E5E0]" data-testid="cart-btn">
          <ShoppingCart className="h-4 w-4" />
          {count > 0 && <span className="absolute -top-2 -right-2 h-5 w-5 rounded-full text-white text-xs grid place-items-center" style={{ backgroundColor: accent }} data-testid="cart-count">{count}</span>}
        </Button>
      </SheetTrigger>
      <SheetContent className="bg-white w-full sm:max-w-md flex flex-col">
        <SheetHeader><SheetTitle className="font-display">Your cart</SheetTitle></SheetHeader>
        <div className="flex-1 overflow-y-auto py-4 space-y-4">
          {cart.length === 0 && <p className="text-slate-400 text-center py-10">Your cart is empty.</p>}
          {cart.map((i) => (
            <div key={i.key} className="flex gap-3" data-testid={`cart-item-${i.product_id}`}>
              {i.image ? <img src={i.image} alt="" className="h-16 w-16 rounded-lg object-cover" /> : <div className="h-16 w-16 rounded-lg bg-slate-100" />}
              <div className="flex-1">
                <div className="font-medium text-sm">{i.name}</div>
                {i.variant && <div className="text-xs text-slate-500">{i.variant}</div>}
                <div className="text-sm text-slate-600">{inr(i.price)}</div>
                <div className="flex items-center gap-2 mt-1">
                  <button onClick={() => updateQty(i.key, i.qty - 1)} className="h-6 w-6 rounded border grid place-items-center" data-testid={`dec-${i.product_id}`}><Minus className="h-3 w-3" /></button>
                  <span className="text-sm w-6 text-center">{i.qty}</span>
                  <button onClick={() => updateQty(i.key, i.qty + 1)} disabled={i.stock && i.qty >= i.stock} className="h-6 w-6 rounded border grid place-items-center disabled:opacity-40" data-testid={`inc-${i.product_id}`}><Plus className="h-3 w-3" /></button>
                  {i.stock && i.qty >= i.stock ? <span className="text-[10px] text-amber-600">max</span> : null}
                  <button onClick={() => updateQty(i.key, 0)} className="ml-auto text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
        {cart.length > 0 && (
          <div className="border-t pt-4 space-y-3">
            <div className="flex justify-between font-semibold"><span>Subtotal</span><span>{inr(total)}</span></div>
            <Button className="w-full text-white" style={{ backgroundColor: accent }} onClick={goCheckout} data-testid="checkout-btn">Checkout</Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function StoreFooter() {
  const { store, slug, cart, accent, categories, setCatFilter, setCartOpen } = useStore();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [catsOpen, setCatsOpen] = useState(false);
  const count = cart.reduce((a, i) => a + i.qty, 0);
  const wa = waLink(store.branding?.whatsapp);
  const pickCat = (c) => { setCatFilter(c); setCatsOpen(false); navigate(`/${slug}`); };
  const Item = ({ icon: Icon, label, onClick, testid, badge }) => (
    <button onClick={onClick} data-testid={testid} className="relative flex-1 flex flex-col items-center gap-0.5 py-2 text-slate-500 hover:text-slate-900 transition-colors">
      <Icon className="h-5 w-5" />
      <span className="text-[10px] font-medium">{label}</span>
      {badge > 0 && <span className="absolute top-1 right-1/4 h-4 w-4 rounded-full text-white text-[9px] grid place-items-center" style={{ backgroundColor: accent }}>{badge}</span>}
    </button>
  );
  return (
    <>
      {catsOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center sm:justify-center" onClick={() => setCatsOpen(false)}>
          <div className="bg-white w-full sm:max-w-sm sm:rounded-2xl rounded-t-2xl p-5 max-h-[70vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="cat-sheet">
            <div className="flex items-center justify-between mb-3"><h3 className="font-display font-semibold text-lg">Categories</h3><button onClick={() => setCatsOpen(false)}><X className="h-5 w-5 text-slate-400" /></button></div>
            <button onClick={() => pickCat("all")} className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-slate-50 text-sm" data-testid="footer-cat-all">All products</button>
            {categories.map((c) => (
              <button key={c} onClick={() => pickCat(c)} className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-slate-50 text-sm" data-testid={`footer-cat-${c}`}>{c}</button>
            ))}
            {categories.length === 0 && <p className="text-slate-400 text-sm px-3 py-2">No categories yet.</p>}
          </div>
        </div>
      )}
      <nav className="fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-md border-t border-[#E5E5E0] flex sm:hidden" data-testid="store-footer">
        <Item icon={HomeIcon} label="Home" testid="footer-home" onClick={() => navigate(`/${slug}`)} />
        <Item icon={LayoutGrid} label="Categories" testid="footer-categories" onClick={() => setCatsOpen(true)} />
        <Item icon={ShoppingCart} label="Cart" testid="footer-cart" badge={count} onClick={() => setCartOpen(true)} />
        {wa && <Item icon={MessageCircle} label="WhatsApp" testid="footer-whatsapp" onClick={() => window.open(wa, "_blank")} />}
        <Item icon={User} label="Account" testid="footer-account" onClick={() => navigate(user?.role === "customer" ? `/${slug}/account` : `/${slug}/login`)} />
      </nav>
      {/* Desktop footer bar */}
      <div className="hidden sm:flex items-center justify-center gap-6 py-4 border-t border-[#E5E5E0] text-sm text-slate-500 mt-8" data-testid="store-footer-desktop">
        <button onClick={() => setCatsOpen(true)} className="flex items-center gap-1.5 hover:text-slate-900" data-testid="footer-categories-desktop"><LayoutGrid className="h-4 w-4" />Categories</button>
        <button onClick={() => setCartOpen(true)} className="flex items-center gap-1.5 hover:text-slate-900" data-testid="footer-cart-desktop"><ShoppingCart className="h-4 w-4" />Cart ({count})</button>
        <button onClick={() => navigate(user?.role === "customer" ? `/${slug}/account` : `/${slug}/login`)} className="flex items-center gap-1.5 hover:text-slate-900" data-testid="footer-account-desktop"><User className="h-4 w-4" />My Account</button>
        {wa && <button onClick={() => window.open(wa, "_blank")} className="flex items-center gap-1.5 hover:text-emerald-600" data-testid="footer-whatsapp-desktop"><MessageCircle className="h-4 w-4" />WhatsApp</button>}
      </div>
    </>
  );
}

function ProductCard({ p }) {
  const { slug, accent, addToCart } = useStore();
  const variants = p.variants || [];
  const [sel, setSel] = useState(null);
  const out = p.status === "out_of_stock" || (variants.length ? variants.every((v) => Number(v.stock) <= 0) : p.stock <= 0);
  const price = sel ? Number(sel.price) : p.price * (1 - (p.discount || 0) / 100);
  const image = (sel && sel.image) || p.images?.[0];
  const disable = out || (variants.length > 0 && !sel);
  return (
    <div className="group bg-white rounded-xl border border-[#E5E5E0] overflow-hidden hover:shadow-md transition-shadow flex flex-col" data-testid={`product-card-${p.id}`}>
      <Link to={`/${slug}/product/${p.id}`} className="block relative aspect-square overflow-hidden bg-slate-50">
        {image && <img src={image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" data-testid={`card-image-${p.id}`} />}
        {p.discount > 0 && !sel && <Badge className="absolute top-2 left-2 text-white" style={{ backgroundColor: accent }}>-{p.discount}%</Badge>}
        {out && <div className="absolute inset-0 bg-white/60 grid place-items-center font-medium text-slate-600">Out of stock</div>}
      </Link>
      <div className="p-4 flex flex-col flex-1">
        <div className="text-xs text-slate-400 uppercase tracking-wider">{p.category}</div>
        <Link to={`/${slug}/product/${p.id}`} className="font-medium mt-1 block hover:underline line-clamp-1">{p.name}</Link>
        {variants.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`card-variants-${p.id}`}>
            {variants.map((v) => {
              const vOut = Number(v.stock) <= 0;
              const active = sel?.name === v.name;
              return (
                <button key={v.id || v.name} disabled={vOut} onClick={() => setSel(active ? null : v)} data-testid={`card-variant-${p.id}-${v.name}`}
                  className={`px-2 py-0.5 rounded-md border text-[11px] transition-colors ${vOut ? "opacity-40 line-through" : active ? "text-white border-transparent" : "bg-white border-[#E5E5E0] text-slate-600"}`}
                  style={active ? { backgroundColor: accent } : {}}>{v.name}</button>
              );
            })}
          </div>
        )}
        <div className="flex items-center justify-between mt-3 pt-1">
          <div className="font-display font-bold" data-testid={`card-price-${p.id}`}>{inr(price)}{p.discount > 0 && !sel && <span className="text-slate-400 text-sm line-through ml-1">{inr(p.price)}</span>}</div>
          <Button size="sm" disabled={disable} className="h-8 text-white" style={{ backgroundColor: accent }}
            onClick={() => addToCart(p, 1, sel ? { price: sel.price, variant: sel.name, label: `${p.name} — ${sel.name}`, stock: sel.stock, image: sel.image || p.images?.[0] } : { stock: p.stock })}
            data-testid={`add-${p.id}`}><Plus className="h-4 w-4" /></Button>
        </div>
      </div>
    </div>
  );
}

function Home() {
  const { store, accent, slug, query, catFilter, setCatFilter, categories } = useStore();
  const [products, setProducts] = useState([]);
  useEffect(() => {
    const t = setTimeout(() => {
      api.get(`/store/${slug}/products`, { params: { q: query || undefined, category: catFilter } }).then((r) => setProducts(r.data.products));
    }, 200);
    return () => clearTimeout(t);
  }, [slug, query, catFilter]);
  return (
    <>
      <section className="relative h-56 sm:h-72 overflow-hidden">
        <img src={store.branding?.banner || "https://images.unsplash.com/photo-1605513524006-063ed6ed31e7?auto=format&fit=crop&w=1600&q=80"} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent grid content-center px-6 sm:px-12">
          <div className="max-w-7xl mx-auto w-full">
            <span className="text-white/70 text-xs uppercase tracking-[0.2em]">Welcome to</span>
            <h1 className="font-display text-3xl sm:text-5xl font-bold text-white tracking-tight mt-2">{store.branding?.store_name}</h1>
            <p className="text-white/80 mt-2 max-w-md">Curated products, delivered to your door.</p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-wrap gap-2 mb-8 overflow-x-auto no-scrollbar">
          <button onClick={() => setCatFilter("all")} className={`px-4 py-1.5 rounded-full text-sm border transition-colors shrink-0 ${catFilter === "all" ? "text-white border-transparent" : "bg-white border-[#E5E5E0] text-slate-600"}`} style={catFilter === "all" ? { backgroundColor: accent } : {}} data-testid="cat-all">All</button>
          {categories.map((c) => (
            <button key={c} onClick={() => setCatFilter(c)} className={`px-4 py-1.5 rounded-full text-sm border transition-colors shrink-0 ${catFilter === c ? "text-white border-transparent" : "bg-white border-[#E5E5E0] text-slate-600"}`} style={catFilter === c ? { backgroundColor: accent } : {}} data-testid={`cat-${c}`}>{c}</button>
          ))}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
          {products.map((p) => <ProductCard key={p.id} p={p} />)}
        </div>
        {products.length === 0 && <p className="text-center text-slate-400 py-16">No products found.</p>}
      </div>
    </>
  );
}

function ProductDetail() {
  const { pid } = useParams();
  const { slug, accent, addToCart } = useStore();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [selVariant, setSelVariant] = useState(null);
  const [activeImg, setActiveImg] = useState(null);
  const [review, setReview] = useState({ rating: 5, comment: "" });
  const load = () => api.get(`/store/${slug}/products/${pid}`).then((r) => setData(r.data));
  useEffect(() => { load(); setSelVariant(null); setActiveImg(null); window.scrollTo(0, 0); }, [slug, pid]);
  if (!data) return <div className="p-10 text-center text-slate-400">Loading…</div>;
  const p = data.product;
  const variants = p.variants || [];
  const packs = p.packs || [];
  const hasVariants = variants.length > 0;
  const basePrice = p.price * (1 - (p.discount || 0) / 100);
  const price = selVariant ? Number(selVariant.price) : basePrice;
  const currentStock = selVariant ? Number(selVariant.stock) : p.stock;
  const gallery = p.images || [];
  const mainImage = activeImg || gallery[0];
  const out = p.status === "out_of_stock" || (hasVariants ? (selVariant ? currentStock <= 0 : false) : currentStock <= 0);
  const disableAdd = out || (hasVariants && !selVariant);
  const submitReview = async () => {
    if (!user || user.role !== "customer") { toast.info("Sign in to review"); navigate(`/${slug}/login`); return; }
    try { await api.post("/customer/reviews", { product_id: pid, rating: review.rating, comment: review.comment }); toast.success("Review posted"); setReview({ rating: 5, comment: "" }); load(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      <Link to={`/${slug}`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-6"><ArrowLeft className="h-4 w-4" />Back to store</Link>
      <div className="grid md:grid-cols-2 gap-10">
        <div>
          <div className="aspect-square rounded-2xl overflow-hidden bg-white border border-[#E5E5E0]">{mainImage && <img src={mainImage} alt={p.name} className="w-full h-full object-cover transition-opacity duration-200" data-testid="pdp-main-image" />}</div>
          {gallery.length > 1 && (
            <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar" data-testid="pdp-gallery">
              {gallery.map((img, i) => {
                const active = (activeImg || gallery[0]) === img;
                return (
                  <button key={i} onClick={() => setActiveImg(img)} data-testid={`pdp-thumb-${i}`}
                    className={`shrink-0 h-16 w-16 rounded-lg overflow-hidden border-2 transition-all ${active ? "" : "border-transparent opacity-70 hover:opacity-100"}`}
                    style={active ? { borderColor: accent } : {}}>
                    <img src={img} alt="" className="h-full w-full object-cover" />
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div>
          <div className="text-xs text-slate-400 uppercase tracking-wider">{p.category}</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">{p.name}</h1>
          <div className="flex items-center gap-1 mt-2 text-sm text-slate-500">{p.review_count > 0 ? <><Star className="h-4 w-4 fill-amber-400 text-amber-400" />{p.rating} · {p.review_count} reviews</> : "No reviews yet"}</div>
          <div className="font-display text-3xl font-bold mt-4">{inr(price)}{!selVariant && p.discount > 0 && <span className="text-slate-400 text-lg line-through ml-2">{inr(p.price)}</span>}</div>
          {p.short_description && <p className="text-slate-500 mt-2">{p.short_description}</p>}
          <p className="text-slate-600 mt-4 leading-relaxed whitespace-pre-line">{p.long_description || p.description}</p>
          {variants.length > 0 && (
            <div className="mt-5">
              <Label className="text-slate-500">Choose an option</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {variants.map((v) => {
                  const vOut = Number(v.stock) <= 0;
                  const active = selVariant?.name === v.name;
                  return (
                    <button key={v.id || v.name} onClick={() => { if (!vOut) { setSelVariant(v); if (v.image) setActiveImg(v.image); } }} disabled={vOut} data-testid={`variant-opt-${v.name}`}
                      className={`px-4 py-2 rounded-lg border text-sm transition-colors ${vOut ? "opacity-40 line-through cursor-not-allowed bg-white border-[#E5E5E0]" : active ? "text-white border-transparent" : "bg-white border-[#E5E5E0] text-slate-700"}`}
                      style={active ? { backgroundColor: accent } : {}}>
                      {v.name} · {inr(v.price)}{vOut ? " · Sold out" : ""}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="text-sm mt-4 text-slate-500">
            {hasVariants && !selVariant ? "Select an option to continue"
              : out ? <span className="text-rose-600">Out of stock</span>
              : currentStock <= (p.low_stock_threshold || 5) ? <span className="text-amber-600" data-testid="pdp-low-stock">Hurry! Only {currentStock} left</span>
              : `${currentStock} in stock`}
          </div>
          <Button disabled={disableAdd} className="mt-4 w-full sm:w-auto text-white px-10" style={{ backgroundColor: accent }}
            onClick={() => addToCart(p, 1, selVariant ? { price: selVariant.price, variant: selVariant.name, label: `${p.name} — ${selVariant.name}`, stock: selVariant.stock, image: selVariant.image || p.images?.[0] } : { stock: p.stock })}
            data-testid="add-to-cart-detail"><ShoppingCart className="h-4 w-4 mr-2" />Add to cart</Button>

          {packs.length > 0 && (
            <div className="mt-8">
              <h3 className="font-display font-semibold">Value packs</h3>
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                {packs.map((pk) => (
                  <div key={pk.id || pk.name} className="flex items-center justify-between p-3 rounded-xl border border-[#E5E5E0] bg-white" data-testid={`pack-opt-${pk.name}`}>
                    <div><div className="font-medium text-sm">{pk.name}</div><div className="text-slate-500 text-xs">{pk.qty} units · {inr(pk.price)}</div></div>
                    <Button size="sm" disabled={out} className="h-8 text-white" style={{ backgroundColor: accent }}
                      onClick={() => addToCart(p, 1, { price: pk.price, variant: pk.name, label: `${p.name} — ${pk.name}`, stock: p.stock })} data-testid={`add-pack-${pk.name}`}><Plus className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {data.related?.length > 0 && (
        <div className="mt-14">
          <h2 className="font-display text-xl font-semibold mb-4">You may also like</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
            {data.related.map((rp) => (
              <Link key={rp.id} to={`/${slug}/product/${rp.id}`} className="bg-white rounded-xl border border-[#E5E5E0] overflow-hidden hover:shadow-md transition-shadow" data-testid={`related-card-${rp.id}`}>
                <div className="aspect-square bg-slate-50">{rp.images?.[0] && <img src={rp.images[0]} alt="" className="w-full h-full object-cover" />}</div>
                <div className="p-3"><div className="text-sm font-medium line-clamp-1">{rp.name}</div><div className="font-display font-bold text-sm mt-1">{inr(rp.price)}</div></div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="mt-14 max-w-2xl">
        <h2 className="font-display text-xl font-semibold">Reviews</h2>
        <div className="mt-4 p-5 bg-white rounded-xl border border-[#E5E5E0]">
          <div className="flex gap-1 mb-3">{[1, 2, 3, 4, 5].map((n) => <button key={n} onClick={() => setReview({ ...review, rating: n })} data-testid={`star-${n}`}><Star className={`h-6 w-6 ${n <= review.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} /></button>)}</div>
          <textarea value={review.comment} onChange={(e) => setReview({ ...review, comment: e.target.value })} placeholder="Share your thoughts…" rows={2} className="w-full border rounded-lg p-3 text-sm" data-testid="review-comment" />
          <Button size="sm" className="mt-3 text-white" style={{ backgroundColor: accent }} onClick={submitReview} data-testid="submit-review">Post review</Button>
        </div>
        <div className="mt-6 space-y-4">
          {data.reviews.map((r) => (
            <div key={r.id} className="border-b border-[#E5E5E0] pb-4">
              <div className="flex items-center gap-2"><span className="font-medium">{r.customer_name}</span><div className="flex">{[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`h-3.5 w-3.5 ${n <= r.rating ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />)}</div></div>
              <p className="text-slate-600 text-sm mt-1">{r.comment}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Checkout() {
  const { slug, cart, accent, clearCart, store } = useStore();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [addr, setAddr] = useState({ line1: "", city: "", state: "", zip: "", phone: "" });
  const [savedAddrs, setSavedAddrs] = useState([]);
  const [payment, setPayment] = useState("upi");
  const [coupon, setCoupon] = useState("");
  const [discount, setDiscount] = useState(0);
  const [shipping, setShipping] = useState(store.shipping_zones?.[0]?.rate || 5);
  const [placing, setPlacing] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (user?.role !== "customer") return;
    api.get("/customer/profile").then((r) => {
      const list = r.data.addresses || [];
      setSavedAddrs(list);
      if (list.length) setAddr({ ...list[0] });
    }).catch(() => {});
  }, [user]);

  useEffect(() => {
    const methods = store.payment_methods || ["upi", "card", "cod"];
    setPayment((cur) => (methods.includes(cur) ? cur : methods[0]));
  }, [store]);

  if (!user || user.role !== "customer") return <Navigate to={`/${slug}/login`} />;
  const subtotal = cart.reduce((a, i) => a + i.price * i.qty, 0);
  const disc = subtotal * discount / 100;
  const total = subtotal - disc + shipping;

  const applyCoupon = async () => {
    try { const { data } = await api.get(`/store/${slug}/coupon/${coupon}`); setDiscount(data.discount_percent); toast.success(`${data.discount_percent}% off applied`); }
    catch (e) { setDiscount(0); toast.error(apiError(e)); }
  };
  const placeOrder = async () => {
    if (!addr.line1 || !addr.city) { toast.error("Please enter your address"); return; }
    setPlacing(true);
    try {
      const { data } = await api.post(`/store/${slug}/checkout`, {
        items: cart.map((i) => ({ product_id: i.product_id, qty: i.qty, variant: i.variant, unit_price: i.price, label: i.name })),
        coupon_code: discount ? coupon : null, payment_method: payment, shipping, address: addr,
      });
      clearCart();
      setDone(data);
      toast.success("Order placed!");
    } catch (e) { toast.error(apiError(e)); } finally { setPlacing(false); }
  };

  if (done) return (
    <div className="max-w-md mx-auto px-6 py-20 text-center">
      <CheckCircle2 className="h-16 w-16 mx-auto" style={{ color: accent }} />
      <h1 className="font-display text-3xl font-bold mt-4">Order confirmed!</h1>
      <p className="text-slate-500 mt-2">Order <span className="font-mono">{done.order_no}</span> · {inr(done.total)}</p>
      <p className="text-slate-400 text-sm mt-1">Payment: {done.payment_method.toUpperCase()} ({done.payment_status})</p>
      <div className="flex gap-3 justify-center mt-8">
        <Button variant="outline" onClick={() => navigate(`/${slug}/account`)} data-testid="view-orders">Track order</Button>
        <Button className="text-white" style={{ backgroundColor: accent }} onClick={() => navigate(`/${slug}`)} data-testid="continue-shopping">Keep shopping</Button>
      </div>
    </div>
  );

  if (cart.length === 0) return <div className="text-center py-20 text-slate-400">Your cart is empty. <Link to={`/${slug}`} className="underline">Shop now</Link></div>;

  return (
    <div className="max-w-5xl mx-auto px-6 py-8 grid lg:grid-cols-5 gap-8">
      <div className="lg:col-span-3 space-y-6">
        <h1 className="font-display text-3xl font-bold tracking-tight">Checkout</h1>
        <div className="bg-white rounded-xl border border-[#E5E5E0] p-6 space-y-3">
          <h3 className="font-semibold">Shipping address</h3>
          {savedAddrs.length > 0 && (
            <div className="space-y-2" data-testid="saved-addresses">
              <Label className="text-slate-500 text-xs">Saved addresses</Label>
              <div className="flex flex-wrap gap-2">
                {savedAddrs.map((a) => {
                  const active = addr.id === a.id;
                  return (
                    <button key={a.id} onClick={() => setAddr({ ...a })} data-testid={`saved-addr-${a.id}`}
                      className={`text-left px-3 py-2 rounded-lg border text-xs max-w-[220px] transition-colors ${active ? "text-white border-transparent" : "bg-white border-[#E5E5E0] text-slate-600"}`}
                      style={active ? { backgroundColor: accent } : {}}>
                      <div className="font-medium">{a.label || a.line1}</div>
                      <div className={active ? "text-white/80" : "text-slate-400"}>{a.line1}, {a.city} {a.zip}</div>
                    </button>
                  );
                })}
                <button onClick={() => setAddr({ line1: "", city: "", state: "", zip: "", phone: "" })} data-testid="add-new-address"
                  className="px-3 py-2 rounded-lg border border-dashed border-[#E5E5E0] text-xs text-slate-500 hover:text-slate-800">+ New address</button>
              </div>
            </div>
          )}
          <Input placeholder="Address line" value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} data-testid="addr-line1" />
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="City" value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} data-testid="addr-city" />
            <Input placeholder="State" value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value })} data-testid="addr-state" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="ZIP" value={addr.zip} onChange={(e) => setAddr({ ...addr, zip: e.target.value })} data-testid="addr-zip" />
            <Input placeholder="Phone" value={addr.phone} onChange={(e) => setAddr({ ...addr, phone: e.target.value })} data-testid="addr-phone" />
          </div>
          {store.shipping_zones?.length > 0 && (
            <div><Label className="text-slate-500">Shipping method</Label>
              <Select value={String(shipping)} onValueChange={(v) => setShipping(Number(v))}><SelectTrigger data-testid="shipping-select"><SelectValue /></SelectTrigger>
                <SelectContent>{store.shipping_zones.map((z) => <SelectItem key={z.id} value={String(z.rate)}>{z.name} — {inr(z.rate)}</SelectItem>)}</SelectContent></Select>
            </div>
          )}
        </div>
        <div className="bg-white rounded-xl border border-[#E5E5E0] p-6 space-y-3">
          <h3 className="font-semibold">Payment method</h3>
          {[["upi", "UPI", CreditCard], ["card", "Credit / Debit Card", CreditCard], ["cod", "Cash on Delivery", Truck]]
            .filter(([v]) => (store.payment_methods || ["upi", "card", "cod"]).includes(v))
            .map(([v, l, Icon]) => (
            <button key={v} onClick={() => setPayment(v)} className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${payment === v ? "border-transparent text-white" : "border-[#E5E5E0]"}`} style={payment === v ? { backgroundColor: accent } : {}} data-testid={`pay-${v}`}>
              <Icon className="h-5 w-5" />{l}{v !== "cod" && <span className={`ml-auto text-xs ${payment === v ? "text-white/70" : "text-slate-400"}`}>Mock payment</span>}
            </button>
          ))}
        </div>
      </div>
      <div className="lg:col-span-2">
        <div className="bg-white rounded-xl border border-[#E5E5E0] p-6 sticky top-20">
          <h3 className="font-semibold mb-4">Order summary</h3>
          <div className="space-y-2 max-h-48 overflow-y-auto">{cart.map((i) => <div key={i.key} className="flex justify-between text-sm"><span className="text-slate-600">{i.name} × {i.qty}</span><span>{inr(i.price * i.qty)}</span></div>)}</div>
          <div className="flex gap-2 mt-4">
            <Input placeholder="Coupon code" value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} className="font-mono" data-testid="coupon-input" />
            <Button variant="outline" onClick={applyCoupon} data-testid="apply-coupon">Apply</Button>
          </div>
          <div className="mt-4 space-y-1.5 text-sm border-t pt-4">
            <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>{inr(subtotal)}</span></div>
            {discount > 0 && <div className="flex justify-between text-emerald-600"><span>Discount ({discount}%)</span><span>-{inr(disc)}</span></div>}
            <div className="flex justify-between text-slate-600"><span>Shipping</span><span>{inr(shipping)}</span></div>
            <div className="flex justify-between font-display font-bold text-lg pt-2 border-t"><span>Total</span><span>{inr(total)}</span></div>
          </div>
          <Button disabled={placing} className="w-full mt-5 text-white h-11" style={{ backgroundColor: accent }} onClick={placeOrder} data-testid="place-order">{placing ? "Placing…" : `Pay ${inr(total)}`}</Button>
        </div>
      </div>
    </div>
  );
}

function Account() {
  const { slug, accent } = useStore();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("orders");
  const load = () => api.get(`/store/${slug}/orders`).then((r) => setOrders(r.data));
  useEffect(() => { if (user?.role === "customer") load(); }, [user]);
  if (!user || user.role !== "customer") return <Navigate to={`/${slug}/login`} />;

  const cancel = async (id) => { try { await api.post(`/customer/orders/${id}/cancel`); toast.success("Order cancelled"); load(); } catch (e) { toast.error(apiError(e)); } };
  const ret = async (id) => { try { await api.post(`/customer/orders/${id}/return`, { reason: "Customer request" }); toast.success("Return requested"); load(); } catch (e) { toast.error(apiError(e)); } };
  const reorder = (o) => { toast.info("Add items from the store to reorder"); navigate(`/${slug}`); };

  const badge = { pending: "bg-amber-100 text-amber-800", confirmed: "bg-blue-100 text-blue-800", packed: "bg-purple-100 text-purple-800", shipped: "bg-indigo-100 text-indigo-800", delivered: "bg-emerald-100 text-emerald-800", cancelled: "bg-rose-100 text-rose-800", returned: "bg-rose-100 text-rose-800" };
  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <h1 className="font-display text-3xl font-bold tracking-tight">Hi, {user.name}</h1>
      <div className="flex gap-2 mt-6 mb-6">
        <button onClick={() => setTab("orders")} className={`px-4 py-2 rounded-lg text-sm ${tab === "orders" ? "text-white" : "bg-white border border-[#E5E5E0]"}`} style={tab === "orders" ? { backgroundColor: accent } : {}} data-testid="tab-orders">Orders</button>
      </div>
      <div className="space-y-4">
        {orders.length === 0 && <p className="text-slate-400 text-center py-12">No orders yet. <Link to={`/${slug}`} className="underline">Start shopping</Link></p>}
        {orders.map((o) => (
          <div key={o.id} className="bg-white rounded-xl border border-[#E5E5E0] p-5" data-testid={`order-${o.id}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div><span className="font-mono font-medium">{o.order_no}</span><span className="text-slate-400 text-sm ml-3">{new Date(o.created_at).toLocaleDateString()}</span></div>
              <Badge className={`capitalize ${badge[o.status]}`}>{o.status}</Badge>
            </div>
            <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar">{o.items.map((it, i) => <div key={i} className="flex items-center gap-2 shrink-0 bg-slate-50 rounded-lg p-2 pr-3">{it.image && <img src={it.image} alt="" className="h-10 w-10 rounded object-cover" />}<span className="text-sm">{it.name} ×{it.qty}</span></div>)}</div>
            <div className="flex items-center justify-between mt-4">
              <span className="font-semibold">{inr(o.total)}</span>
              <div className="flex gap-2">
                {["pending", "confirmed"].includes(o.status) && <Button size="sm" variant="outline" className="border-rose-200 text-rose-600 text-xs h-8" onClick={() => cancel(o.id)} data-testid={`cancel-${o.id}`}>Cancel</Button>}
                {o.status === "delivered" && <Button size="sm" variant="outline" className="text-xs h-8" onClick={() => ret(o.id)} data-testid={`return-${o.id}`}>Return</Button>}
                <Button size="sm" variant="outline" className="text-xs h-8" onClick={() => reorder(o)} data-testid={`reorder-${o.id}`}>Reorder</Button>
              </div>
            </div>
            <div className="flex items-center gap-1 mt-3 text-xs text-slate-400">
              {["pending", "confirmed", "packed", "shipped", "delivered"].map((s, i) => {
                const active = o.timeline?.some((t) => t.status === s);
                return <div key={s} className="flex items-center gap-1">{i > 0 && <span className="w-4 h-px bg-slate-200" />}<span className={active ? "font-medium" : ""} style={active ? { color: accent } : {}}>{s}</span></div>;
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StoreAuth() {
  const { slug, accent, store } = useStore();
  const { applyAuth, user } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ name: "", phone: "" });
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");

  useEffect(() => { if (user?.role === "customer") navigate(`/${slug}`); }, [user]);

  const sendOtp = async () => {
    if (!f.phone || f.phone.replace(/[^0-9]/g, "").length < 10) { toast.error("Enter a valid mobile number"); return; }
    try { const { data } = await api.post("/auth/otp/request", { phone: f.phone }); setOtpSent(true); toast.success(`OTP sent (demo: ${data.dev_otp})`); }
    catch (e) { toast.error(apiError(e)); }
  };
  const verifyOtp = async () => {
    try { const { data } = await api.post("/auth/otp/verify", { phone: f.phone, otp, name: f.name, vendor_slug: slug }); applyAuth(data); toast.success("Signed in"); navigate(`/${slug}`); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-16">
      <Link to={`/${slug}`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-6"><ArrowLeft className="h-4 w-4" />Back to {store.branding?.store_name}</Link>
      <div className="bg-white rounded-2xl border border-[#E5E5E0] p-8">
        <h1 className="font-display text-2xl font-bold">{otpSent ? "Verify your number" : "Login / Register"}</h1>
        <p className="text-slate-500 text-sm mt-1">{otpSent ? `We sent a code to ${f.phone}` : "Sign in with your mobile number — new? we'll create your account automatically."}</p>
        <div className="space-y-3 mt-6">
          {!otpSent ? (
            <>
              <div><Label>Mobile number</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="+91 98765 43210" data-testid="otp-phone" /></div>
              <div><Label>Name <span className="text-slate-400">(new users)</span></Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Your name" data-testid="otp-name" /></div>
              <Button className="w-full text-white h-11" style={{ backgroundColor: accent }} onClick={sendOtp} data-testid="send-otp">Send OTP</Button>
            </>
          ) : (
            <>
              <div><Label>Enter OTP</Label><Input value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="4-digit code" data-testid="otp-code" /></div>
              <Button className="w-full text-white h-11" style={{ backgroundColor: accent }} onClick={verifyOtp} data-testid="verify-otp">Verify & continue</Button>
              <button className="text-sm text-slate-500 hover:text-slate-900 w-full" onClick={() => setOtpSent(false)} data-testid="otp-change-number">Change number</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
